import React, { FC, useEffect, useRef } from 'react';
import { RouteComponentProps, useHistory } from 'react-router-dom';
import { Button, Brand } from '@clearcover/atlas';
import { AnimatedScreen } from 'Shared/components';
import { reportEvent } from 'Shared/analytics';
import { WheelCanvas } from './components/WheelCanvas/WheelCanvas';
import { OutcomeModal } from './components/OutcomeModal/OutcomeModal';
import { useSpinWheel } from './useSpinWheel';
import { DISPLAY_DEFAULTS_BY_CODE } from 'Rewards/services/endpoints/GetRewardsConfig/types';
import styles from './styles.module.css';

export const SpinWheel: FC<RouteComponentProps> = () => {
  const history = useHistory();

  const returnToPortal = () => {
    reportEvent('26for26-spin-wheel:return-to-portal');
    history.push('/rewards');
  };

  const {
    config,
    outcomes,
    status,
    spinState,
    pendingResult,
    pendingOutcomeConfig,
    attemptError,
    hasRemainingEntries,
    remainingEntries,
    wheelCanvasRef,
    spin,
    onWheelRest,
    dismissOutcome,
    startNewAttempt,
  } = useSpinWheel();

  // Fire display event only once the wheel is ready to be interacted with —
  // not during loading or error states, which are transient and don't represent
  // a meaningful "viewed" impression.
  const hasReportedDisplay = useRef(false);
  useEffect(() => {
    if (
      !hasReportedDisplay.current &&
      (spinState === 'idle' || spinState === 'no_entries')
    ) {
      hasReportedDisplay.current = true;
      reportEvent('26for26-spin-wheel:display');
    }
  }, [spinState]);

  const isLoading = spinState === 'loading' || spinState === 'posting';
  const isError = spinState === 'error';

  return (
    <div className={styles.page}>
      <div className={styles.wheelChrome}>
        <Brand
          variant="wordmark"
          color="white"
          height={24}
          className={styles.logo}
        />
        {status && (
          <div className={styles.entriesBadge} data-testid="entries-badge">
            <span className={styles.entriesCount}>
              {status.availableEntries}
            </span>{' '}
            <span className={styles.entriesLabel}>
              {status.availableEntries === 1 ? 'entry' : 'entries'}
            </span>
          </div>
        )}
      </div>
      <AnimatedScreen
        loading={isLoading}
        error={
          isError ? 'Unable to load the spin wheel. Please try again.' : null
        }
        testId="spin-wheel"
      >
        <div className={styles.layout}>
          <header className={styles.header}>
            <p className={styles.eyebrow}>
              {config?.phase.name ?? "26 for '26"}
            </p>
            <h1 className={styles.title}>
              Spin to <span className={styles.titleAccent}>Win</span>
            </h1>
          </header>

          {config && outcomes.length > 0 && (
            <div className={styles.wheelContainer}>
              <div
                className={styles.pointer}
                aria-hidden="true"
                data-testid="wheel-pointer"
              />
              <div className={styles.wheelWrapper}>
                <WheelCanvas
                  ref={wheelCanvasRef}
                  outcomes={outcomes}
                  onRest={onWheelRest}
                />
              </div>
            </div>
          )}

          <div className={styles.actions}>
            {spinState === 'no_entries' ? (
              <p
                className={styles.noEntriesMessage}
                data-testid="no-entries-message"
              >
                You have no spins available. Bind more policies to earn entries.
              </p>
            ) : (
              <>
                <Button
                  variant="primary"
                  className={styles.spinButton}
                  disabled={spinState !== 'idle'}
                  onClick={() => {
                    reportEvent('26for26-spin-wheel:spin:click');
                    spin();
                  }}
                  data-testid="spin-button"
                >
                  {spinState === 'spinning' ? 'Spinning…' : 'Spin'}
                </Button>
                {attemptError && (
                  <p
                    className={styles.attemptError}
                    data-testid="attempt-error"
                  >
                    Something went wrong. Try spinning again.
                  </p>
                )}
                {status && (
                  <p className={styles.entryCount} data-testid="entry-count">
                    {status.availableEntries === 1
                      ? 'You have 1 entry ready to play'
                      : `You have ${status.availableEntries} entries ready to play`}
                  </p>
                )}
              </>
            )}
          </div>

          {config && outcomes.length > 0 && (
            <ul className={styles.prizeLegend} data-testid="prize-legend">
              {[...outcomes]
                .sort((a, b) => a.weight - b.weight)
                .map((outcome) => {
                  const dotColor =
                    DISPLAY_DEFAULTS_BY_CODE[outcome.code]?.legendDotColor ??
                    outcome.display.color;
                  return (
                    <li key={outcome.code} className={styles.prizeLegendItem}>
                      <span
                        className={styles.prizeLegendDot}
                        style={{ background: dotColor }}
                      />
                      {outcome.prizeAmount != null ? (
                        <span className={styles.prizeLegendAmount}>
                          ${outcome.prizeAmount}
                        </span>
                      ) : (
                        <span className={styles.prizeLegendLabel}>
                          {DISPLAY_DEFAULTS_BY_CODE[outcome.code]
                            ?.legendLabel ?? outcome.code}
                        </span>
                      )}{' '}
                      <span className={styles.prizeLegendWeight}>
                        {outcome.weight}%
                      </span>
                    </li>
                  );
                })}
            </ul>
          )}

          {/* Progress bar hidden — uncomment to restore
          {status && spinState !== 'no_entries' && (
            <div className={styles.progress} data-testid="progress-bar">
              <p className={styles.progressLabel}>
                {status.progress.policiesUntilNextEntry === 0
                  ? 'Next entry earned!'
                  : `${status.progress.policiesUntilNextEntry} more ${
                      status.progress.policiesUntilNextEntry === 1
                        ? 'policy'
                        : 'policies'
                    } to your next spin`}
              </p>
              <div className={styles.progressTrack}>
                <div
                  className={styles.progressFill}
                  style={{
                    width: `${
                      (Math.max(
                        0,
                        POLICIES_PER_ENTRY -
                          status.progress.policiesUntilNextEntry
                      ) /
                        POLICIES_PER_ENTRY) *
                      100
                    }%`,
                  }}
                />
              </div>
            </div>
          )}
          */}

          <button
            className={styles.returnLink}
            onClick={returnToPortal}
            data-testid="return-to-portal"
          >
            Return to Portal
          </button>
        </div>

        {spinState === 'revealed' && pendingResult && (
          <OutcomeModal
            result={pendingResult}
            isWin={pendingOutcomeConfig?.isWin ?? false}
            // Bonus-entry treatment is specific to the CONSOLATION tier in
            // ACT1/ACT2. NONE (ACT3's non-win) falls through to the plain
            // no-prize branch. The backend's outcome code is the source of
            // truth — match on that, not on a coarse flag.
            isConsolation={pendingOutcomeConfig?.code === 'CONSOLATION'}
            outcomeHeading={pendingOutcomeConfig?.display.modalHeading}
            outcomeDescription={pendingOutcomeConfig?.display.description}
            hasRemainingEntries={hasRemainingEntries}
            remainingEntries={remainingEntries}
            onClose={() => {
              reportEvent('26for26-spin-wheel:outcome-modal:close');
              dismissOutcome();
              returnToPortal();
            }}
            onSpinAgain={() => {
              reportEvent('26for26-spin-wheel:outcome-modal:spin-again');
              // Stay on the wheel screen. startNewAttempt fires a fresh
              // POST /attempts (the same outer-button call SPIN NOW makes),
              // hydrates the new PENDING's outcome into local state, and
              // drops us back to 'idle' so the inner Spin button accepts it.
              startNewAttempt();
            }}
          />
        )}
      </AnimatedScreen>
    </div>
  );
};
